/** Plain range values and pure transformations. Navigation drafts may be unpinned;
 * Drupal writes require a translation ID and revision together. */
import { ensureInt } from "./util/strings";

export type WordOrdinalsData = string | Array<number> | null;

export interface VidRangeData {
  fromVid: number;
  toVid: number;
  tid?: string | null;
  tr?: string;
  wordOrdinals?: WordOrdinalsData;
}

export interface TranslationAgnosticVidRange {
  toVid: number;
  fromVid: number;
}

export interface VidRange {
  fromVid: number;
  toVid: number;
  tid: string | null;
  tr: string | undefined;
  wordOrdinals: number[];
}

/**
 * Create a plain range with ascending inclusive VID bounds and a fresh word-selection array.
 * Translation-neutral ranges discard the revision; no server validation or revision lookup occurs.
 */
export function makeRange(
  fromVid: number,
  toVid: number,
  tid: string | null = null,
  wordOrdinals: WordOrdinalsData = null,
  tr?: string,
): VidRange {
  return {
    fromVid: Math.min(fromVid, toVid),
    toVid: Math.max(fromVid, toVid),
    tid,
    tr: tid ? tr : undefined,
    wordOrdinals: parseWordOrdinals(wordOrdinals),
  };
}

/**
 * Compare bounds, translation, revision, and ordered word selections.
 * Serialized selections are parsed before comparison; a missing other range is unequal.
 */
export function rangesEqual(
  range: VidRange,
  another: VidRange | VidRangeData | null | undefined,
): boolean {
  if (!another) {
    return false;
  }
  return (
    range.fromVid === another.fromVid &&
    range.toVid === another.toVid &&
    range.tid === (another.tid ?? null) &&
    range.tr === another.tr &&
    range.wordOrdinals.join(",") ===
      parseWordOrdinals(another.wordOrdinals || null).join(",")
  );
}

/**
 * Copy a range, including its word-selection array, without sharing mutable selection state.
 */
export function cloneRange(range: VidRange): VidRange {
  return makeRange(
    range.fromVid,
    range.toVid,
    range.tid,
    range.wordOrdinals,
    range.tr,
  );
}

/**
 * Test whether the inclusive VID bounds identify a single VID.
 */
export function isSingleVerse(range: VidRange): boolean {
  return range.fromVid === range.toVid;
}

/**
 * Serialize only VID bounds for navigation, omitting translation, revision, and selected words.
 */
export function rangeToVidsString(range: VidRange): string {
  if (isSingleVerse(range)) {
    return `${range.fromVid}`;
  } else {
    return `${range.fromVid}-${range.toVid}`;
  }
}

/**
 * Test inclusive VID overlap without considering translation or revision.
 */
export function rangesOverlap(
  range: VidRange,
  target: TranslationAgnosticVidRange,
): boolean {
  return (
    Math.max(range.fromVid, target.fromVid) <=
    Math.min(range.toVid, target.toVid)
  );
}

/**
 * Serialize a range for internal storage, encoding selected words as comma-separated ordinals.
 * This does not enforce the stricter Drupal write contract.
 */
export function dumpRange(range: VidRange): VidRangeData {
  return {
    fromVid: range.fromVid,
    toVid: range.toVid,
    tid: range.tid,
    tr: range.tr,
    wordOrdinals: range.wordOrdinals.join(","),
  };
}

/**
 * Copy a range with a translation and optional revision.
 * Changing translations or replacing an existing revision clears selected words.
 * Omitting the revision preserves it only when the translation is unchanged; clearing the
 * translation also clears the revision.
 */
export function withTranslation(
  range: VidRange,
  tid: string | null,
  tr = tid === range.tid ? range.tr : undefined,
): VidRange {
  return makeRange(
    range.fromVid,
    range.toVid,
    tid,
    tid === range.tid && (!range.tr || tr === range.tr)
      ? range.wordOrdinals
      : [],
    tr,
  );
}

/**
 * Copy a range with a freshly parsed word selection, preserving its bounds and revision.
 */
export function withWordOrdinals(
  range: VidRange,
  ordinals: WordOrdinalsData,
): VidRange {
  return makeRange(range.fromVid, range.toVid, range.tid, ordinals, range.tr);
}

/**
 * Subtract inclusive VID bounds, returning zero, one, or two fresh ranges.
 * Unchanged ranges keep their selected words; trimmed segments retain translation and
 * revision but clear word selections.
 */
export function subtractRange(
  range: VidRange,
  other: TranslationAgnosticVidRange,
): [VidRange, VidRange] | [VidRange] | [] {
  if (!rangesOverlap(range, other)) {
    return [cloneRange(range)];
  }
  let front: VidRange | null = null;
  if (range.fromVid <= other.fromVid - 1) {
    front = makeRange(
      range.fromVid,
      other.fromVid - 1,
      range.tid,
      null,
      range.tr,
    );
  }
  let back: VidRange | null = null;
  if (other.toVid + 1 <= range.toVid) {
    back = makeRange(other.toVid + 1, range.toVid, range.tid, null, range.tr);
  }
  if (front && back) {
    // The VID other was split in two, because verses were removed in the middle.
    return [front, back];
  }
  if (front) {
    // Verses were removed from the end.
    return [front];
  }
  if (back) {
    // Verses were removed from the front.
    return [back];
  }
  // All verses were removed.
  return [];
}

/**
 * Return the smallest interval covering both ranges, keeping the first range's translation
 * and revision and clearing selected words, even when the bounds do not change.
 */
export function expandRange(
  range: VidRange,
  other: TranslationAgnosticVidRange,
): VidRange {
  return makeRange(
    Math.min(range.fromVid, other.fromVid),
    Math.max(range.toVid, other.toVid),
    range.tid,
    null,
    range.tr,
  );
}

/**
 * Compare ordered range lists, including translation revisions and selected words.
 */
export function someRangesDiffer(a: VidRange[], b: VidRange[]): boolean {
  if (a.length !== b.length) {
    return true;
  }
  for (let i = 0; i < a.length; i++) {
    const r1 = a[i];
    const r2 = b[i];
    if (r1 && r2 && !rangesEqual(r1, r2)) {
      return true;
    }
  }
  return false;
}

/**
 * Compare optional ranges, treating null and undefined as the same absent value.
 */
export function rangeOrNullDiffers(
  a: VidRange | null | undefined,
  b: VidRange | null | undefined,
): boolean {
  return !((!a && !b) || (a && b && rangesEqual(a, b)));
}

/**
 * Parse comma-separated VID intervals into unpinned ranges in the supplied translation.
 * This permissive navigation parser skips unusable entries and normalizes reversed bounds;
 * it is not a validator for stored scripture fields.
 */
export function parseVidsString(vids: string, tid: string | null): VidRange[] {
  return vids
    .split(",")
    .filter((s) => s.length)
    .map((s) => {
      const parts = s
        .split("-")
        .map((n) => parseInt(n))
        .filter((n) => Number.isInteger(n));
      if (!parts[0] || parts.length > 2) {
        return null;
      }
      if (!parts[1]) {
        return makeRange(parts[0], parts[0], tid);
      }
      return makeRange(parts[0], parts[1], tid);
    })
    .filter((r): r is VidRange => r !== null);
}

/**
 * Copy numeric selections or parse comma-separated word ordinals; null and empty text yield [].
 * Order and duplicates are preserved, and parsed values are not checked for bounds or NaN.
 */
export function parseWordOrdinals(value: WordOrdinalsData): number[] {
  if (typeof value === "string") {
    if (value.length < 1) {
      return [];
    } else {
      // Split the string on the commas and parse the integers.
      return value.split(",").map((o) => parseInt(o));
    }
  } else if (value === null) {
    return [];
  } else {
    return value.map(ensureInt);
  }
}

/**
 * Convert stored range data to a plain range, normalizing bounds and copying selected words.
 * Preserve an explicit revision without resolving or validating it against the server.
 */
export function createVidRange(data: VidRangeData): VidRange {
  const fromVid = data.fromVid;
  const toVid = data.toVid;
  const wordOrdinals = data.wordOrdinals;
  return makeRange(
    ensureInt(fromVid),
    ensureInt(toVid),
    data.tid || null,
    wordOrdinals || null,
    data.tr,
  );
}
